import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

/* ═══════════════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════════════ */

export interface RateEntry {
  currencyCode: string;
  symbol:       string;
  name:         string;
  rate:         number;
  base:         string;
  updatedAtISO: string | null;
  countryCodes: string[];
}

export interface RatesState {
  rates:      Record<string, number>;   // { USD: 1, EUR: 0.92, NGN: 1580 ... }
  symbols:    Record<string, string>;   // { USD: "$", EUR: "€" ... }
  names:      Record<string, string>;   // { USD: "US Dollar" ... }
  countryMap: Record<string, string>;   // { US: "USD", NG: "NGN" ... }
  updatedAt:  string | null;
  status:     "idle" | "loading" | "succeeded" | "failed";
  error:      string | null;
}

const initialState: RatesState = {
  rates:      {},
  symbols:    {},
  names:      {},
  countryMap: {},
  updatedAt:  null,
  status:     "idle",
  error:      null,
};

/* ═══════════════════════════════════════════════════════════
   COUNTRY → CURRENCY MAP
   Built from Firestore docs (each doc has countryCodes[])
═══════════════════════════════════════════════════════════ */

function buildCountryMap(docs: RateEntry[]): Record<string, string> {
  const map: Record<string, string> = {};
  docs.forEach(({ currencyCode, countryCodes }) => {
    (countryCodes || []).forEach(cc => { map[cc] = currencyCode; });
  });
  return map;
}

/* ═══════════════════════════════════════════════════════════
   fetchRates — one-time fetch from Cosmoola currency API
   Web version: uses fetch() against the getCurrencyRates endpoint.
   Call this on app start to get rates immediately.
   Auth: pass your Cosmoola pk_ key via env.
═══════════════════════════════════════════════════════════ */

export const fetchRates = createAsyncThunk<
  Omit<RatesState, "status" | "error">,
  void,
  { rejectValue: string }
>(
  "rates/fetchRates",
  async (_, { rejectWithValue }) => {
    try {
      const apiKey = process.env.REACT_APP_COSMOOLA_PUBLISHABLE_KEY || "";

      /* Detect visitor country via ipapi (same as Cosmoola SDK does) */
      let country = "";
      try {
        const geo = await fetch("https://ipapi.co/json/", {
          signal: AbortSignal.timeout(3000),
        });
        const geoData = await geo.json();
        country = geoData.country_code || "";
      } catch {
        /* fallback: use browser locale */
        const lang = navigator.language || "";
        const parts = lang.split("-");
        country = parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "";
      }

      const qs = country ? `?country=${country}` : "";
      const res = await fetch(
        `https://cosmoola.com/currency/getCurrencyRates${qs}`,
        {
          headers: {
            Authorization: `Bearer ${apiKey}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        return rejectWithValue(
          errData?.error?.message ?? `HTTP ${res.status}`
        );
      }

      const data = await res.json();

      /*
       * data shape from backend:
       * { rates: {USD:1, EUR:0.92 ...}, symbols: {...}, names: {...},
       *   countryMap: {...}, detected: "NGN", updatedAt: "..." }
       */

      const docs: RateEntry[] = Object.keys(data.rates || {}).map(code => ({
        currencyCode: code,
        symbol:       data.symbols?.[code] ?? code,
        name:         data.names?.[code]   ?? code,
        rate:         data.rates[code],
        base:         "USD",
        updatedAtISO: data.updatedAt ?? null,
        countryCodes: [],
      }));

      return {
        rates:      data.rates      ?? {},
        symbols:    data.symbols    ?? {},
        names:      data.names      ?? {},
        countryMap: data.countryMap ?? buildCountryMap(docs),
        updatedAt:  data.updatedAt  ?? null,
      };
    } catch (err: any) {
      console.error("[rates/fetchRates]", err);
      return rejectWithValue(err.message ?? "Failed to fetch rates");
    }
  }
);

/* ═══════════════════════════════════════════════════════════
   listenToRates — Web version: polls the Cosmoola API on an
   interval (no native Firestore SDK on web in this setup).
   Call ONCE in your app root. Returns a cleanup function.

   Usage:
     const stop = listenToRates(dispatch);
     // call stop() on unmount if needed

   Polls every 60 minutes (rates don't change frequently).
   Change POLL_INTERVAL_MS to suit your needs.
═══════════════════════════════════════════════════════════ */

const POLL_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

export function listenToRates(
  dispatch: (action: any) => void
): () => void {
  /* Fetch immediately on start */
  dispatch(fetchRates());

  /* Then poll on interval */
  const timer = window.setInterval(() => {
    dispatch(fetchRates());
  }, POLL_INTERVAL_MS);

  return () => window.clearInterval(timer);
}

/* ═══════════════════════════════════════════════════════════
   SLICE
═══════════════════════════════════════════════════════════ */

const ratesSlice = createSlice({
  name: "rates",
  initialState,
  reducers: {
    /* Called manually if you want to inject snapshot data */
    setRatesSnapshot: (
      state,
      action: PayloadAction<Omit<RatesState, "status" | "error">>
    ) => {
      state.rates      = action.payload.rates;
      state.symbols    = action.payload.symbols;
      state.names      = action.payload.names;
      state.countryMap = action.payload.countryMap;
      state.updatedAt  = action.payload.updatedAt;
      state.status     = "succeeded";
      state.error      = null;
    },

    clearRates: (state) => {
      Object.assign(state, initialState);
    },
  },

  extraReducers: builder => {
    builder
      .addCase(fetchRates.pending, state => {
        state.status = "loading";
        state.error  = null;
      })
      .addCase(fetchRates.fulfilled, (state, action) => {
        state.rates      = action.payload.rates;
        state.symbols    = action.payload.symbols;
        state.names      = action.payload.names;
        state.countryMap = action.payload.countryMap;
        state.updatedAt  = action.payload.updatedAt;
        state.status     = "succeeded";
        state.error      = null;
      })
      .addCase(fetchRates.rejected, (state, action) => {
        state.status = "failed";
        state.error  = action.payload ?? "Failed to fetch rates";
      });
  },
});

export const { setRatesSnapshot, clearRates } = ratesSlice.actions;
export default ratesSlice.reducer;

/* ═══════════════════════════════════════════════════════════
   SELECTORS
═══════════════════════════════════════════════════════════ */

export const selectRates      = (s: any) => s.rates.rates      as Record<string, number>;
export const selectSymbols    = (s: any) => s.rates.symbols    as Record<string, string>;
export const selectNames      = (s: any) => s.rates.names      as Record<string, string>;
export const selectCountryMap = (s: any) => s.rates.countryMap as Record<string, string>;
export const selectRatesReady = (s: any) => s.rates.status === "succeeded";
export const selectUpdatedAt  = (s: any) => s.rates.updatedAt  as string | null;

/* ═══════════════════════════════════════════════════════════
   UTILITIES — identical API to the RN version
═══════════════════════════════════════════════════════════ */

/** Convert any amount between two currencies using rates from Redux */
export function convertAmount(
  amount:  number,
  from:    string,
  to:      string,
  rates:   Record<string, number>
): number {
  if (!rates || from === to) return amount;
  const fromRate = rates[from] || 1;
  const toRate   = rates[to]   || 1;
  return (amount / fromRate) * toRate;
}

/** Format with symbol, correct decimal places */
export function formatAmount(
  amount:  number,
  code:    string,
  symbols: Record<string, string>
): string {
  const symbol     = symbols[code] || code;
  const noDecimals = new Set([
    "JPY","KRW","IDR","VND","CLP","ISK","PYG","UGX","TZS",
    "BIF","RWF","GNF","MGA","KMF","XAF","XOF","XPF","DJF",
  ]);
  const decimals  = noDecimals.has(code) ? 0 : 2;
  const formatted = amount
    .toFixed(decimals)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${symbol}${formatted}`;
}